import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

const AUTOPLAY_INTERVAL = 3500;

/**
 * The showcase section: three desktop-style windows holding a photo, a small
 * photo viewer that steps through its pictures, and a video with its own
 * play key. On phones the windows sit in a swipeable row with a taskbar.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [frames] - The scrollable row of windows.
 * @property {HTMLElement[]} [frame] - Each window.
 * @property {HTMLElement[]} [photo] - The viewer's photos.
 * @property {HTMLElement} [count] - The "1 / 4" readout.
 * @property {HTMLVideoElement} [video] - The video.
 * @property {HTMLButtonElement} [playButton] - The play / pause key.
 * @property {HTMLElement} [progress] - The video progress fill.
 * @property {HTMLButtonElement[]} [tab] - The taskbar keys.
 *
 * @extends {Component<Refs>}
 */
class ShowcaseComponent extends Component {
  #index = 0;
  /** @type {number | undefined} */
  #timer;
  #userPaused = prefersReducedMotion();
  /** @type {IntersectionObserver | undefined} */
  #observer;
  #abort = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abort;
    const { video, frames } = this.refs;

    // Hold still while someone is pointing at the viewer or tabbing through it.
    this.addEventListener('mouseenter', this.#stop, { signal });
    this.addEventListener('mouseleave', this.#start, { signal });
    this.addEventListener('focusin', this.#stop, { signal });
    this.addEventListener('focusout', this.#start, { signal });

    video?.addEventListener('play', this.#syncPlayButton, { signal });
    video?.addEventListener('pause', this.#syncPlayButton, { signal });
    video?.addEventListener('timeupdate', this.#syncProgress, { signal });
    frames?.addEventListener('scroll', this.#syncTabs, { signal, passive: true });

    // Run the viewer and the video only while the section is on screen.
    this.#observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          this.#start();
          if (!this.#userPaused) video?.play().catch(() => {});
        } else {
          this.#stop();
          video?.pause();
        }
      },
      { threshold: 0.25 }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stop();
    this.#observer?.disconnect();
    this.#abort.abort();
  }

  /** Shows the previous photo in the viewer. */
  previous() {
    this.#show(this.#index - 1);
    this.#start();
  }

  /** Shows the next photo in the viewer. */
  next() {
    this.#show(this.#index + 1);
    this.#start();
  }

  /** Plays or pauses the video; a manual pause sticks until the visitor plays it again. */
  toggleVideo() {
    const { video } = this.refs;
    if (!video) return;

    this.#userPaused = !video.paused;
    if (video.paused) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }

  /**
   * Scrolls the window that belongs to the pressed taskbar key into view.
   * @param {Event} event - The click event from a taskbar key.
   */
  showFrame(event) {
    const { tab = [], frame = [], frames } = this.refs;
    const target = event.target instanceof Element ? event.target.closest('button') : null;
    const index = tab.findIndex((button) => button === target);
    const window = frame[index];
    if (!frames || !window) return;

    frames.scrollTo({ left: window.offsetLeft - frames.offsetLeft, behavior: 'smooth' });
    this.#pressTab(index);
  }

  /**
   * @param {number} index - The photo to show; wraps around at both ends.
   */
  #show(index) {
    const { photo = [], count } = this.refs;
    if (!photo.length) return;

    this.#index = (index + photo.length) % photo.length;
    photo.forEach((element, position) => {
      element.hidden = position !== this.#index;
    });
    if (count) count.textContent = `${this.#index + 1} / ${photo.length}`;
  }

  #start = () => {
    this.#stop();
    const { photo = [] } = this.refs;
    if (!this.hasAttribute('autoplay') || prefersReducedMotion() || photo.length < 2) return;

    this.#timer = window.setInterval(() => this.#show(this.#index + 1), AUTOPLAY_INTERVAL);
  };

  #stop = () => {
    window.clearInterval(this.#timer);
  };

  #syncPlayButton = () => {
    const { video, playButton } = this.refs;
    if (!video || !playButton) return;

    playButton.textContent = video.paused ? '▶' : '❚❚';
    const label = video.paused ? playButton.dataset.labelPlay : playButton.dataset.labelPause;
    if (label) playButton.setAttribute('aria-label', label);
  };

  #syncProgress = () => {
    const { video, progress } = this.refs;
    if (!video || !progress) return;

    progress.style.width = `${(video.currentTime / video.duration) * 100 || 0}%`;
  };

  #syncTabs = () => {
    const { frames, frame = [] } = this.refs;
    if (!frames) return;

    const middle = frames.scrollLeft + frames.clientWidth / 2;
    const index = frame.findIndex((element) => {
      const left = element.offsetLeft - frames.offsetLeft;
      return left <= middle && middle < left + element.offsetWidth;
    });
    if (index >= 0) this.#pressTab(index);
  };

  /**
   * @param {number} index - The taskbar key to mark as current.
   */
  #pressTab(index) {
    const { tab = [] } = this.refs;
    tab.forEach((button, position) => button.setAttribute('aria-pressed', String(position === index)));
  }
}

if (!customElements.get('showcase-component')) {
  customElements.define('showcase-component', ShowcaseComponent);
}
